import {
  filterStateForViewer,
  initGame,
  type MinusAuctionAction,
  type MinusAuctionState,
  type MinusAuctionViewerState,
  reduceGameState,
  validateAction,
} from "@cloudnest/minus-auction-engine";
import { useSyncExternalStore } from "react";
import { isPlatformGamePath } from "../../shared/routing/game-path-mode";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
  type GameEvent,
  type ViewerStateRow,
} from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function resolveMinusAuctionGameApiMode(): GameApiMode {
  if (typeof window === "undefined") return "legacy";
  return isPlatformGamePath("minus-auction", window.location.pathname)
    ? "game-platform"
    : "legacy";
}

function getGameApi(): GameApi {
  const nextMode = resolveMinusAuctionGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("minus-auction", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

interface MinusAuctionMultiplayerState {
  readonly gameState: MinusAuctionViewerState | null;
  readonly roomId: string | null;
  readonly myParticipantId: string | null;
  readonly isHost: boolean;
  readonly isObserver: boolean;
  readonly lastAppliedSeq: number;
  readonly lastError: string | null;
}

const initialState: MinusAuctionMultiplayerState = {
  gameState: null,
  roomId: null,
  myParticipantId: null,
  isHost: false,
  isObserver: false,
  lastAppliedSeq: 0,
  lastError: null,
};

let state: MinusAuctionMultiplayerState = { ...initialState };
let canonicalState: MinusAuctionState | null = null;
let catchUpInFlight = false;

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

function setError(error: unknown, fallback: string): void {
  state = {
    ...state,
    lastError: error instanceof Error ? error.message : fallback,
  };
  notify();
}

function projectCanonicalState(): MinusAuctionViewerState | null {
  if (!canonicalState) {
    return null;
  }
  const viewerId = state.myParticipantId ?? getCurrentUserId() ?? "";
  return filterStateForViewer(canonicalState, viewerId);
}

function applyLegacyEvent(
  gameState: MinusAuctionState | null,
  eventType: string,
  payload: Record<string, unknown>,
): MinusAuctionState | null {
  if (eventType === "init") {
    return payload.state as unknown as MinusAuctionState;
  }
  if (!gameState) {
    return null;
  }
  if (eventType === "minus_auction_action") {
    return reduceGameState(
      gameState,
      payload.action as unknown as MinusAuctionAction,
    );
  }
  console.warn(`Unknown minus-auction event type: ${eventType}`);
  return gameState;
}

interface ReplayResult {
  readonly gameState: MinusAuctionState | null;
  readonly lastAppliedSeq: number;
}

function replayFromEvents(events: readonly GameEvent[]): ReplayResult {
  let gameState: MinusAuctionState | null = null;
  let lastAppliedSeq = 0;

  for (const event of events) {
    gameState = applyLegacyEvent(
      gameState,
      event.eventType,
      event.payload as Record<string, unknown>,
    );
    lastAppliedSeq = event.sequenceNumber;
  }

  return { gameState, lastAppliedSeq };
}

function getOptionalNumber(
  config: Record<string, unknown> | undefined,
  key: string,
): number | undefined {
  const value = config?.[key];
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function adoptLatest(rows: readonly ViewerStateRow[], fromSeq: number): number {
  let bestSeq = fromSeq;
  let best: Record<string, unknown> | null = null;
  for (const row of rows) {
    if (row.sequenceNumber > bestSeq) {
      bestSeq = row.sequenceNumber;
      best = row.state;
    }
  }

  if (best !== null) {
    const viewerState = best as unknown as MinusAuctionViewerState;
    state = {
      ...state,
      gameState: viewerState,
      myParticipantId: viewerState.viewerPlayerId,
      lastAppliedSeq: bestSeq,
      lastError: null,
    };
  }
  return bestSeq;
}

async function fetchViewerStates(
  roomId: string,
  after = state.lastAppliedSeq,
): Promise<void> {
  const res = await getGameApi().getViewerStates(roomId, after);
  if (res.success && res.data && state.roomId === roomId) {
    adoptLatest(res.data, after);
    notify();
  }
}

function isGamePlatformMode(): boolean {
  return resolveMinusAuctionGameApiMode() === "game-platform";
}

export const minusAuctionMultiplayerActions = {
  initializeAsHost: async (
    roomId: string,
    playerIds: readonly string[],
    hostPlayerId: string,
    config: Record<string, unknown> | undefined,
  ): Promise<void> => {
    const mode = resolveMinusAuctionGameApiMode();
    canonicalState = null;
    state = {
      ...initialState,
      roomId,
      myParticipantId: getCurrentUserId() ?? hostPlayerId,
      isHost: true,
      isObserver: false,
    };
    notify();

    if (mode === "game-platform") {
      try {
        const existing = await getGameApi().getViewerStates(roomId);
        const existingRows =
          existing.success && existing.data ? existing.data : [];
        if (existingRows.length > 0) {
          adoptLatest(existingRows, 0);
          notify();
          return;
        }

        const initResponse = await getGameApi().submitEvent(roomId, "init", {});
        if (!initResponse.success) {
          state = {
            ...state,
            lastError: initResponse.error ?? "게임 초기화에 실패했습니다",
          };
          notify();
          return;
        }
        await fetchViewerStates(roomId, 0);
      } catch (error) {
        setError(error, "게임 초기화에 실패했습니다");
      }
      return;
    }

    try {
      const eventsResponse = await getGameApi().getGameEvents(roomId);
      const existingEvents = eventsResponse.success
        ? (eventsResponse.data ?? [])
        : [];

      if (existingEvents.length > 0) {
        const replayed = replayFromEvents(existingEvents);
        canonicalState = replayed.gameState;
        state = {
          ...state,
          gameState: projectCanonicalState(),
          lastAppliedSeq: replayed.lastAppliedSeq,
          lastError: null,
        };
        notify();
        return;
      }
    } catch (error) {
      setError(error, "이벤트를 불러올 수 없습니다");
      return;
    }

    const nextCanonicalState = initGame({
      gameId: roomId,
      playerIds,
      hostPlayerId,
      seed: getOptionalNumber(config, "seed"),
      startingChips: getOptionalNumber(config, "startingChips"),
      startingGarnets: getOptionalNumber(config, "startingGarnets"),
    });

    canonicalState = nextCanonicalState;
    state = {
      ...state,
      gameState: projectCanonicalState(),
      lastAppliedSeq: 0,
      lastError: null,
    };
    notify();

    try {
      const response = await getGameApi().submitEvent(roomId, "init", {
        state: nextCanonicalState as unknown as Record<string, unknown>,
      });
      if (response.success && response.data) {
        state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
        notify();
      }
    } catch (error) {
      setError(error, "게임 초기화에 실패했습니다");
    }
  },

  initializeAsGuest: async (roomId: string): Promise<void> => {
    canonicalState = null;
    state = {
      ...initialState,
      roomId,
      myParticipantId: getCurrentUserId(),
      isHost: false,
      isObserver: false,
    };
    notify();

    if (isGamePlatformMode()) {
      try {
        await fetchViewerStates(roomId, 0);
      } catch (error) {
        setError(error, "이벤트를 불러올 수 없습니다");
      }
    }
  },

  initializeAsObserver: async (roomId: string): Promise<void> => {
    canonicalState = null;
    state = {
      ...initialState,
      roomId,
      myParticipantId: null,
      isHost: false,
      isObserver: true,
    };
    notify();

    if (!isGamePlatformMode()) {
      return;
    }

    try {
      await fetchViewerStates(roomId, 0);
    } catch (error) {
      setError(error, "관전 상태를 불러올 수 없습니다");
    }
  },

  replayEvents: async (roomId: string): Promise<void> => {
    if (isGamePlatformMode()) {
      try {
        await fetchViewerStates(roomId, state.lastAppliedSeq);
      } catch (error) {
        setError(error, "이벤트를 불러올 수 없습니다");
      }
      return;
    }

    try {
      const response = await getGameApi().getGameEvents(roomId);
      if (!response.success || !response.data) return;

      const replayed = replayFromEvents(response.data);
      canonicalState = replayed.gameState;
      state = {
        ...state,
        gameState: projectCanonicalState(),
        lastAppliedSeq: replayed.lastAppliedSeq,
        lastError: null,
      };
      notify();
    } catch (error) {
      setError(error, "이벤트를 불러올 수 없습니다");
    }
  },

  applyRemoteEvent: (
    eventType: string,
    payload: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;

    if (isGamePlatformMode()) {
      if (state.roomId) {
        void fetchViewerStates(state.roomId, state.lastAppliedSeq);
      }
      return;
    }

    if (!canonicalState && eventType !== "init") return;
    canonicalState = applyLegacyEvent(canonicalState, eventType, payload);
    state = {
      ...state,
      gameState: projectCanonicalState(),
      lastAppliedSeq: seq,
      lastError: null,
    };
    notify();
  },

  catchUpEvents: async (roomId: string): Promise<void> => {
    if (catchUpInFlight) return;

    catchUpInFlight = true;
    try {
      if (isGamePlatformMode()) {
        await fetchViewerStates(roomId, state.lastAppliedSeq);
        return;
      }
      if (!canonicalState || canonicalState.phase === "GAME_OVER") return;
      const response = await getGameApi().getGameEvents(
        roomId,
        state.lastAppliedSeq,
      );
      if (!response.success || !response.data) return;
      for (const event of response.data) {
        minusAuctionMultiplayerActions.applyRemoteEvent(
          event.eventType,
          event.payload as Record<string, unknown>,
          event.sequenceNumber,
        );
      }
    } catch {
      // Transient network miss; the next polling tick or reconnect retries.
    } finally {
      catchUpInFlight = false;
    }
  },

  submitAction: async (action: MinusAuctionAction): Promise<void> => {
    if (!state.gameState || !state.roomId) return;
    if (state.isObserver) {
      state = { ...state, lastError: "관전자는 행동할 수 없습니다." };
      notify();
      return;
    }

    const roomId = state.roomId;
    if (!isGamePlatformMode()) {
      if (!canonicalState) return;
      const validation = validateAction(canonicalState, action);
      if (!validation.valid) {
        state = {
          ...state,
          lastError: validation.error ?? "지금은 실행할 수 없는 행동입니다.",
        };
        notify();
        return;
      }
    }

    try {
      const response = await getGameApi().submitEvent(
        roomId,
        "minus_auction_action",
        { action: action as unknown as Record<string, unknown> },
      );
      if (!response.success) {
        state = {
          ...state,
          lastError: response.error ?? "행동 전송에 실패했습니다",
        };
        notify();
        void minusAuctionMultiplayerActions.catchUpEvents(roomId);
        return;
      }
      if (isGamePlatformMode()) {
        await fetchViewerStates(roomId, state.lastAppliedSeq);
      }
      state = { ...state, lastError: null };
      notify();
    } catch (error) {
      setError(error, "행동 전송에 실패했습니다");
    }
  },

  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  reset: (): void => {
    catchUpInFlight = false;
    canonicalState = null;
    state = { ...initialState };
    notify();
  },
};

export const minusAuctionMultiplayerStore = {
  getState: (): MinusAuctionMultiplayerState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useMinusAuctionMultiplayer(): MinusAuctionMultiplayerState {
  return useSyncExternalStore(
    minusAuctionMultiplayerStore.subscribe,
    minusAuctionMultiplayerStore.getState,
  );
}

export function useMinusAuctionMultiplayerActions(): typeof minusAuctionMultiplayerActions {
  return minusAuctionMultiplayerActions;
}
